import React, { useContext, useMemo } from 'react'

import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import ImageWithReservedSpace from 'components/Common/ImageWithReservedSpace'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLink from 'components/Luxkit/TextLink'
import { fireInteractionEvent } from 'api/googleTagManager'
import { accountAccessShowLogin } from 'actions/UiActions'
import TrackingContext from 'contexts/trackingContext'
import WalledContent from 'components/Common/WalledContent'
import clsx from 'clsx'
import { DistanceUnit } from 'components/OfferList/OfferListTiles/Support/searchDistanceLabel'
import { getDistanceLabel } from 'lib/customer/recommendationUtils'
import Caption from 'components/Luxkit/Typography/Caption'

const ImageBadges = styled.div`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
`

const ContentTags = styled.div`
  display: flex;

  > * + * {
    margin-left: ${rem(4)};
  }
`

const PriceDetails = styled.div``

const MainDescription = styled.div``

const Section = styled.div`
  > * + * {
    margin-top: ${rem(8)};
  }
`

const Root = styled.div`
  min-width: ${rem(220)};
  > * + * {
    margin-top: ${rem(16)};
  }

  &.bordered {
    border: 1px solid ${({ theme }) => theme.palette.neutral.five};
    border-radius: ${props => props.theme.borderRadius.large};
    overflow: hidden;
    display: flex;
    flex-direction: column;

    ${Section} {
      padding-left: ${rem(20)};
      padding-right: ${rem(20)};
      &:last-child {
        flex: 1;
        display: flex;
        align-items: flex-end;
        padding-bottom: ${rem(20)};
      }
    }
  }
`

const Subsection = styled.div`
  > * + * {
    margin-top: ${rem(4)};
  }
`

const ExternalImg = styled.img`
  height: ${rem(178)};
  width: 100%;
  object-fit: cover;
`

const ExternalContainer = styled.div`
  position: relative;
  width: 100%;
`

const slotFinder = (elements: React.ReactElement | React.ReactElement[]) => {
  const nodes: React.ReactElement[] = [].concat(elements)

  return {
    ImageBadges: nodes.find(child => child.type === ImageBadges),
    ContentTags: nodes.find(child => child.type === ContentTags),
    MainDescription: nodes.find(child => child.type === MainDescription),
    PriceDetails: nodes.find(child => child.type === PriceDetails),
  }
}

export interface Props {
  className?: string;
  image?: App.Image;
  externalImageUrl?: string;
  location: React.ReactNode;
  locationDistance?: number;
  locationDistanceFrom?: string;
  locationDistanceUnit?: DistanceUnit;
  title: string;
  subtitle?: React.ReactNode;
  walledGarden: boolean;
  children: React.ReactElement | React.ReactElement[];
  variant?: 'default' | 'bordered';
  imageXSRatio?: string;
  imageXSWidth?: string;
  imageSMRatio?: string;
  imageSMWidth?: string;
  testId?: string;
}

function BaseYouMayAlsoLikeTile({
  className,
  image,
  externalImageUrl,
  location,
  locationDistance,
  locationDistanceFrom,
  locationDistanceUnit,
  title,
  subtitle,
  walledGarden,
  variant = 'default',
  imageXSRatio = '3:2',
  imageXSWidth = '273px',
  imageSMRatio = '3:2',
  imageSMWidth = '273px',
  testId,
  children,
}: Props) {
  const slots = slotFinder(children)
  const dispatch = useAppDispatch()
  const tracking = useContext(TrackingContext)

  const locationText = useMemo(() => {
    if (!locationDistanceFrom || !locationDistance || !locationDistanceUnit) {
      return null
    }
    return getDistanceLabel(locationDistance, locationDistanceFrom, locationDistanceUnit)
  }, [locationDistance, locationDistanceFrom, locationDistanceUnit])

  return (
    <Root data-testid={testId} className={[clsx({ bordered: variant === 'bordered' }), className].join(' ')}>
      {image && <ImageWithReservedSpace
        id={image.id}
        alt={image.title}
        withPlaceholder
        mobileAspectRatio={imageXSRatio}
        mobileWidth={imageXSWidth}
        tabletAspectRatio={imageSMRatio}
        tabletWidth={imageSMWidth}
      >
        {slots.ImageBadges}
      </ImageWithReservedSpace>}
      {!image && externalImageUrl && <ExternalContainer>
        <ExternalImg src={externalImageUrl} alt={title} />
        {slots.ImageBadges}
      </ExternalContainer>}
      <Section>
        {slots.ContentTags}
        {locationText && <BodyText variant="small" colour="neutral-three" lineClamp={1}>{locationText}</BodyText>}
        <Caption variant="large" weight="bold" lineClamp={1}>{location}</Caption>
        <Subsection>
          <BodyText variant="large" weight="bold" lineClamp={subtitle ? 1 : 3} as="h4">{title}</BodyText>
          {subtitle && <BodyText variant="large" lineClamp={2} as="h4">{subtitle}</BodyText>}
          {slots.MainDescription}
        </Subsection>
      </Section>
      {slots.PriceDetails && (
        <Section>
          <WalledContent enforced={walledGarden} wall={(
            <TextLink
              onClick={(e) => {
                e.preventDefault()
                tracking && fireInteractionEvent({
                  ...tracking,
                  action: 'Login Click',
                })
                dispatch(accountAccessShowLogin())
              }}
            >
              Become a member to see rates
            </TextLink>
          )}>
            {slots.PriceDetails}
          </WalledContent>
        </Section>
      )}
    </Root>
  )
}

BaseYouMayAlsoLikeTile.ImageBadges = ImageBadges
BaseYouMayAlsoLikeTile.ContentTags = ContentTags
BaseYouMayAlsoLikeTile.PriceDetails = PriceDetails
BaseYouMayAlsoLikeTile.MainDescription = MainDescription

export default BaseYouMayAlsoLikeTile
